/** KA kitchen staff phone login — same pattern as DA delivery partners. */

import { applyKitchenStaffSession, clearHubProfile } from "@/lib/kitchenHubProfile";
import {
  loadMirroredSessionToken,
  saveMirroredSessionToken,
  staffSessionApiError,
} from "@/lib/staffSessionStability";

export const KITCHEN_STAFF_TOKEN_KEY = "ttt_kitchen_staff_token";
export const KITCHEN_STAFF_TOKEN_MIRROR_KEY = "ttt_kitchen_staff_token_mirror";
export const KITCHEN_STAFF_PHONE_KEY = "ttt_kitchen_staff_phone";

function parseApiError(data, fallback) {
  const detail = data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || d?.loc?.join(".") || JSON.stringify(d)).join("; ");
  }
  return fallback;
}

export function loadKitchenStaffToken() {
  return loadMirroredSessionToken(KITCHEN_STAFF_TOKEN_KEY, KITCHEN_STAFF_TOKEN_MIRROR_KEY);
}

export function hasKitchenStaffSession() {
  return Boolean(loadKitchenStaffToken());
}

export function saveKitchenStaffToken(token) {
  saveMirroredSessionToken(KITCHEN_STAFF_TOKEN_KEY, KITCHEN_STAFF_TOKEN_MIRROR_KEY, token);
}

export function loadSavedKitchenStaffPhone() {
  try {
    return (localStorage.getItem(KITCHEN_STAFF_PHONE_KEY) || "").trim();
  } catch {
    return "";
  }
}

export function saveKitchenStaffPhone(phone) {
  try {
    const clean = (phone || "").replace(/\D/g, "").slice(-10);
    if (clean) localStorage.setItem(KITCHEN_STAFF_PHONE_KEY, clean);
  } catch {
    /* ignore */
  }
}

export function clearKitchenStaffSession() {
  saveKitchenStaffToken("");
  clearHubProfile();
}

export async function registerKitchenStaff({ name, phone, primaryRole }) {
  const fd = new FormData();
  fd.append("name", name);
  fd.append("phone", phone);
  if (primaryRole) fd.append("primary_role", primaryRole);
  const res = await fetch("/api/production/staff/register", { method: "POST", body: fd });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseApiError(data, "Registration failed"));
  if (data.token) saveKitchenStaffToken(data.token);
  if (phone) saveKitchenStaffPhone(phone);
  if (data.staff) applyKitchenStaffSession(data.staff);
  return data;
}

export async function fetchKitchenStaffMe() {
  const token = loadKitchenStaffToken();
  if (!token) throw staffSessionApiError("Not logged in", 401);
  const params = new URLSearchParams({ token });
  const res = await fetch(`/api/production/staff/me?${params.toString()}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw staffSessionApiError(parseApiError(data, "Could not load profile"), res.status);
  if (data.staff) applyKitchenStaffSession(data.staff);
  return data;
}
